import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import {
  catchError,
  distinctUntilChanged,
  finalize,
  forkJoin,
  of,
  Subscription,
} from 'rxjs';

import {
  CraditService,
  FinancialAnalysisRow,
  FinancialAnalysisSectionJson,
  SaveCreditProposalSectionPayload,
  SelectableFinancialAnalysisRow,
} from '../shared/services/cradit.service';

interface RowIdentity {
  institution: string;
  sortOrder: number;
  fncTypSeq: number;
  fncValSeq: number;
}

/**
 * Rows pre-selected the first time this section is opened (nothing saved
 * yet): the figures of the client's Credit Proposal format. Matched by
 * sortOrder + fncTypSeq + fncValSeq (the descriptions can change), since
 * fncTypSeq/fncValSeq alone repeat across sortOrders. The user can still
 * unselect any of them or select more. Items of that format with no row in
 * get-fnc-anlsys (Total Assets, PMIC Borrowings and its ratios, Profit after
 * Tax, Women Clients, Debt to Equity - PMIC, Uncovered Capital Ratio,
 * Interest Coverage Ratio, Annual Percentage Rate) are not listed.
 */
const DEFAULT_SELECTED_ROWS: ReadonlyArray<{
  sortOrder: number;
  fncTypSeq: number;
  fncValSeq: number;
}> = [
  { sortOrder: 1, fncTypSeq: 3, fncValSeq: 19 }, // Loan Portfolio (PKR) — Loan Portfolio - Net
  { sortOrder: 1, fncTypSeq: 8, fncValSeq: 26.7 }, // Total Liabilities (PKR)
  { sortOrder: 5, fncTypSeq: 2, fncValSeq: 7.5 }, // Total Borrowings (PKR) — Working: Borrowings
  { sortOrder: 1, fncTypSeq: 6, fncValSeq: 22.5 }, // Total Equity (PKR)
  { sortOrder: 5, fncTypSeq: 2, fncValSeq: 13 }, // Active Clients
  { sortOrder: 4, fncTypSeq: 2, fncValSeq: 4 }, // Current Ratio (x)
  { sortOrder: 4, fncTypSeq: 2, fncValSeq: 6 }, // Portfolio to Assets (%)
  { sortOrder: 4, fncTypSeq: 1, fncValSeq: 2 }, // Return on Assets (%)
  { sortOrder: 4, fncTypSeq: 1, fncValSeq: 3 }, // Return on Equity (%)
  { sortOrder: 4, fncTypSeq: 2, fncValSeq: 5 }, // Debt to Equity - Overall (x) — Debt/Equity - NBMFIs
  { sortOrder: 4, fncTypSeq: 5, fncValSeq: 16 }, // Operating Expenses (%) — Overheads Ratio
  { sortOrder: 4, fncTypSeq: 5, fncValSeq: 17 }, // Cost of Fund (%)
  { sortOrder: 4, fncTypSeq: 3, fncValSeq: 8 }, // Portfolio at Risk (>30 days)
  { sortOrder: 4, fncTypSeq: 3, fncValSeq: 9 }, // Write Offs (%)
  { sortOrder: 4, fncTypSeq: 3, fncValSeq: 10 }, // True Infection Ratio (%) — (Gross NPL+Write-offs)/(GLP)
  { sortOrder: 4, fncTypSeq: 3, fncValSeq: 12 }, // Loan Loss Reserve (%)
  { sortOrder: 4, fncTypSeq: 3, fncValSeq: 11 }, // Risk Coverage Ratio (%)
  { sortOrder: 4, fncTypSeq: 1, fncValSeq: 1 }, // Operational Self-Sufficiency — OSS
];

@Component({
  selector: 'app-financial-analysis-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
  ],
  templateUrl: './financial-analysis-details.html',
  styleUrl: './financial-analysis-details.css',
})
export class FinancialAnalysisDetails
  implements OnInit, OnChanges, OnDestroy {

  @Input() sectionId = '';
  @Input() config: any = {};
  @Input() editable = false;
  @Input() crdtPrpslSeq: number | null = null;

  @Output()
  selectedRowsChange =
    new EventEmitter<FinancialAnalysisRow[]>();

  readonly financialAnalysisRefValSeq = 2647;

  onboardingId = 0;
  crdtPrpslBlkSeq: number | null = null;

  rows: SelectableFinancialAnalysisRow[] = [];
  selectedRows: FinancialAnalysisRow[] = [];

  periodColumns: string[] = [];

  loading = false;
  saving = false;

  errorMessage = '';
  successMessage = '';

  selectAllChecked = false;

  private loadedSequence: number | null = null;
  private loadingSequence: number | null = null;

  private hasSavedBlock = false;
  private savedSelectionKeys = new Set<string>();

  private readonly subscriptions =
    new Subscription();

  constructor(
    private creditService: CraditService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.onboardingId =
      this.getOnboardingSequence();

    const sequenceSubscription =
      this.creditService.crdtPrpslSeq$
        .pipe(
          distinctUntilChanged(),
        )
        .subscribe((sequence) => {
          const normalizedSequence =
            Number(sequence ?? 0);

          if (normalizedSequence <= 0) {
            return;
          }

          this.crdtPrpslSeq =
            normalizedSequence;

          this.loadSectionData(
            normalizedSequence,
          );
        });

    this.subscriptions.add(
      sequenceSubscription,
    );

    const saveTriggerSubscription =
      this.creditService.saveSectionTrigger$
        .subscribe((triggeredSectionId) => {
          if (
            triggeredSectionId ===
            this.sectionId
          ) {
            this.saveFinancialAnalysisSection();
          }
        });

    this.subscriptions.add(
      saveTriggerSubscription,
    );

    const serviceSequence =
      this.creditService
        .getCrdtPrpslSeq();

    const currentSequence =
      Number(
        this.crdtPrpslSeq ??
        serviceSequence ??
        0,
      );

    if (currentSequence > 0) {
      this.loadSectionData(
        currentSequence,
      );
    }
  }

  ngOnChanges(
    changes: SimpleChanges,
  ): void {
    if (
      changes['crdtPrpslSeq'] &&
      this.crdtPrpslSeq !== null &&
      Number(this.crdtPrpslSeq) > 0
    ) {
      this.loadSectionData(
        Number(this.crdtPrpslSeq),
      );
    }

    if (
      changes['config'] &&
      !changes['config'].firstChange &&
      this.rows.length > 0
    ) {
      this.applyExistingSelections();
    }

    if (
      changes['editable'] &&
      !this.editable &&
      this.rows.length > 0
    ) {
      this.applyExistingSelections();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get institutionDisplayName(): string {
    const configInstitution =
      String(
        this.config?.institutionName ??
        this.config?.instNm ??
        '',
      ).trim();

    if (configInstitution) {
      return configInstitution;
    }

    const institution =
      this.rows
        .map((row) =>
          String(
            row.institution ?? '',
          ).trim(),
        )
        .find(
          (name) =>
            name.length > 0,
        );

    return institution || '-';
  }

  get selectedCount(): number {
    return this.rows.filter(
      (
        row:
          SelectableFinancialAnalysisRow,
      ) => row.selected,
    ).length;
  }

  /* ====================================================== */
  /* LOAD */
  /* ====================================================== */

  loadSectionData(
    sequence?: number,
    forceReload = false,
  ): void {
    const proposalSequence =
      Number(
        sequence ??
        this.crdtPrpslSeq ??
        this.creditService
          .getCrdtPrpslSeq() ??
        0,
      );

    if (
      !Number.isFinite(
        proposalSequence,
      ) ||
      proposalSequence <= 0
    ) {
      this.resetTable();

      this.errorMessage =
        this.translate.instant('FIN_ANALYSIS.ERR_PROPOSAL_SEQ_UNAVAILABLE');
      this.toastr.error(this.errorMessage);

      return;
    }

    if (
      !forceReload &&
      (
        this.loadingSequence ===
          proposalSequence ||
        this.loadedSequence ===
          proposalSequence
      )
    ) {
      return;
    }

    this.loading = true;

    this.loadingSequence =
      proposalSequence;

    this.errorMessage = '';
    this.successMessage = '';

    const requestSubscription =
      forkJoin({
        analysis:
          this.creditService
            .getFinancialAnalysis(
              proposalSequence,
            ),

        savedSection:
          this.creditService
            .getCrdtBlck(
              proposalSequence,
              [
                this
                  .financialAnalysisRefValSeq,
              ],
            )
            .pipe(
              catchError((error) => {
                console.warn(
                  'Unable to load saved Financial Analysis Details selection:',
                  error,
                );

                return of([]);
              }),
            ),
      })
        .pipe(
          finalize(() => {
            this.loading = false;
            this.loadingSequence = null;
          }),
        )
        .subscribe({
          next: ({ analysis, savedSection }) => {
            const responseRows =
              this.extractRows(
                analysis,
              );

            const savedSelection =
              this.extractSavedSelection(
                savedSection,
              );

            this.crdtPrpslBlkSeq =
              savedSelection.crdtPrpslBlkSeq;

            this.hasSavedBlock =
              savedSelection.hasSavedBlock;

            this.savedSelectionKeys =
              savedSelection.selectionKeys;

            const fallbackKeys =
              this.hasSavedBlock
                ? this.savedSelectionKeys
                : this.getConfigSelectionKeys();

            this.rows =
              responseRows
                .filter(
                  (
                    row:
                      FinancialAnalysisRow,
                  ) =>
                    row !== null &&
                    row !== undefined,
                )
                .sort(
                  (
                    first:
                      FinancialAnalysisRow,
                    second:
                      FinancialAnalysisRow,
                  ) =>
                    Number(
                      first.sortOrder ?? 0,
                    ) -
                    Number(
                      second.sortOrder ?? 0,
                    ),
                )
                .map(
                  (
                    row:
                      FinancialAnalysisRow,
                  ):
                    SelectableFinancialAnalysisRow => {
                    const normalizedRow = {
                      institution:
                        String(
                          row.institution ?? '',
                        ).trim(),

                      sortOrder:
                        Number(
                          row.sortOrder ?? 0,
                        ),

                      fncTypSeq:
                        Number(
                          row.fncTypSeq ?? 0,
                        ),

                      fncValSeq:
                        Number(
                          row.fncValSeq ?? 0,
                        ),

                      head:
                        String(
                          row.head ?? '',
                        ).trim(),

                      dscr:
                        String(
                          row.dscr ?? '',
                        ).trim(),

                      periodValues:
                        this.normalizePeriodValues(
                          row.periodValues,
                        ),
                    };

                    return {
                      ...normalizedRow,

                      selected:
                        fallbackKeys.has(
                          this.buildRowKey(
                            normalizedRow,
                          ),
                        ) ||
                        this.isDefaultSelection(
                          normalizedRow,
                        ),
                    };
                  },
                );

            this.periodColumns =
              this.getPeriodColumns(
                this.rows,
              );

            this.loadedSequence =
              proposalSequence;

            this.updateSelectionState();
          },

          error: (error: any) => {
            console.error(
              'Financial Analysis load error:',
              error,
            );

            this.loadedSequence =
              null;

            this.resetTable();

            this.errorMessage =
              error?.error?.message ??
              this.translate.instant('FIN_ANALYSIS.ERR_LOAD');
            this.toastr.error(this.errorMessage);
          },
        });

    this.subscriptions.add(
      requestSubscription,
    );
  }

  refreshFinancialAnalysis(): void {
    const proposalSequence =
      Number(
        this.crdtPrpslSeq ??
        this.creditService
          .getCrdtPrpslSeq() ??
        0,
      );

    if (proposalSequence <= 0) {
      this.errorMessage =
        this.translate.instant('FIN_ANALYSIS.ERR_PROPOSAL_SEQ_UNAVAILABLE');
      this.toastr.error(this.errorMessage);

      return;
    }

    this.loadSectionData(
      proposalSequence,
      true,
    );
  }

  onSelectAllChange(): void {
    if (!this.editable) {
      return;
    }

    this.rows.forEach((row) => {
      row.selected =
        this.selectAllChecked;
    });

    this.updateSelectedRows();

    this.successMessage = '';
    this.errorMessage = '';
  }

  onRowSelectionChange(): void {
    if (!this.editable) {
      return;
    }

    this.updateSelectionState();

    this.successMessage = '';
    this.errorMessage = '';
  }

  /**
   * Local-only: stages the current checkbox selection into `selectedRows` /
   * `config.selectedRows` and emits it. Persistence happens exclusively
   * through `saveFinancialAnalysisSection()`, triggered by the parent
   * Document Builder page.
   */
  addSelectedRows(): void {
    const selected =
      this.getSelectedRows();

    if (!selected.length) {
      this.errorMessage =
        this.translate.instant('FIN_ANALYSIS.ERR_SELECT_AT_LEAST_ONE');
      this.toastr.error(this.errorMessage);

      this.successMessage = '';

      return;
    }

    this.selectedRows = [
      ...selected,
    ];

    if (!this.config) {
      this.config = {};
    }

    this.config.selectedRows = [
      ...selected,
    ];

    this.selectedRowsChange.emit(
      selected,
    );

    this.errorMessage = '';

    this.successMessage =
      this.translate.instant('FIN_ANALYSIS.ROWS_SELECTED', {
        count: selected.length,
      });
    this.toastr.success(this.successMessage);
  }

  clearSelection(): void {
    if (!this.editable) {
      return;
    }

    this.rows.forEach((row) => {
      row.selected = false;
    });

    this.selectAllChecked = false;
    this.selectedRows = [];

    if (this.config) {
      this.config.selectedRows = [];
    }

    this.selectedRowsChange.emit([]);

    this.successMessage = '';
    this.errorMessage = '';
  }

  getSelectedRows():
    FinancialAnalysisRow[] {
    return this.rows
      .filter(
        (
          row:
            SelectableFinancialAnalysisRow,
        ) => row.selected,
      )
      .map(
        (
          row:
            SelectableFinancialAnalysisRow,
        ):
          FinancialAnalysisRow => ({
          institution:
            row.institution,

          sortOrder:
            row.sortOrder,

          fncTypSeq:
            row.fncTypSeq,

          fncValSeq:
            row.fncValSeq,

          head:
            row.head,

          dscr:
            row.dscr,

          periodValues: {
            ...row.periodValues,
          },
        }),
      );
  }

  getPeriodValue(
    row: SelectableFinancialAnalysisRow,
    period: string,
  ): number | null {
    const value =
      row.periodValues?.[period];

    if (
      value === null ||
      value === undefined
    ) {
      return null;
    }

    const numericValue =
      Number(value);

    return Number.isFinite(
      numericValue,
    )
      ? numericValue
      : null;
  }

  trackByRow(
    index: number,
    row: SelectableFinancialAnalysisRow,
  ): string {
    return [
      row.fncTypSeq,
      row.fncValSeq,
      row.sortOrder,
      index,
    ].join('-');
  }

  trackByPeriod(
    index: number,
    period: string,
  ): string {
    return period;
  }

  isFirstHeadRow(index: number): boolean {
    if (index === 0) {
      return true;
    }

    const currentHead = String(
      this.rows[index]?.head ?? '',
    )
      .trim()
      .toLowerCase();

    const previousHead = String(
      this.rows[index - 1]?.head ?? '',
    )
      .trim()
      .toLowerCase();

    return currentHead !== previousHead;
  }

  getHeadRowSpan(index: number): number {
    if (!this.isFirstHeadRow(index)) {
      return 0;
    }

    const currentHead = String(
      this.rows[index]?.head ?? '',
    )
      .trim()
      .toLowerCase();

    let rowSpan = 1;

    for (
      let currentIndex = index + 1;
      currentIndex < this.rows.length;
      currentIndex++
    ) {
      const nextHead = String(
        this.rows[currentIndex]?.head ?? '',
      )
        .trim()
        .toLowerCase();

      if (nextHead !== currentHead) {
        break;
      }

      rowSpan++;
    }

    return rowSpan;
  }

  /* ====================================================== */
  /* SAVE */
  /* ====================================================== */

  saveFinancialAnalysisSection(): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.sectionId) {
      this.reportValidationError(
        this.translate.instant('FIN_ANALYSIS.ERR_SECTION_ID_REQUIRED'),
      );

      return;
    }

    if (!this.editable) {
      this.reportValidationError(
        this.translate.instant('FIN_ANALYSIS.ERR_NOT_EDITABLE'),
      );

      return;
    }

    const proposalSequence =
      Number(
        this.crdtPrpslSeq ??
        this.creditService
          .getCrdtPrpslSeq() ??
        0,
      );

    if (proposalSequence <= 0) {
      this.reportValidationError(
        this.translate.instant('FIN_ANALYSIS.ERR_PROPOSAL_SEQ_REQUIRED'),
      );

      return;
    }

    if (this.onboardingId <= 0) {
      this.reportValidationError(
        this.translate.instant('FIN_ANALYSIS.ERR_ONBOARDING_SEQ_REQUIRED'),
      );

      return;
    }

    const selected =
      this.getSelectedRows();

    if (!selected.length) {
      this.reportValidationError(
        this.translate.instant('FIN_ANALYSIS.ERR_SELECT_AT_LEAST_ONE'),
      );

      return;
    }

    const hasInvalidRow =
      selected.some(
        (row) =>
          !Number.isFinite(
            Number(row.fncTypSeq),
          ) ||
          Number(row.fncTypSeq) <= 0 ||
          !Number.isFinite(
            Number(row.fncValSeq),
          ) ||
          Number(row.fncValSeq) <= 0,
      );

    if (hasInvalidRow) {
      this.reportValidationError(
        this.translate.instant('FIN_ANALYSIS.ERR_INVALID_ROWS'),
      );

      return;
    }

    if (this.saving) {
      return;
    }

    const cleanRows: FinancialAnalysisRow[] =
      [...selected]
        .sort(
          (first, second) =>
            Number(first.sortOrder ?? 0) -
              Number(second.sortOrder ?? 0) ||
            Number(first.fncTypSeq ?? 0) -
              Number(second.fncTypSeq ?? 0) ||
            Number(first.fncValSeq ?? 0) -
              Number(second.fncValSeq ?? 0),
        )
        .map((row) => ({
          institution: row.institution,
          sortOrder: row.sortOrder,
          fncTypSeq: row.fncTypSeq,
          fncValSeq: row.fncValSeq,
          head: row.head,
          dscr: row.dscr,
          periodValues: {
            ...row.periodValues,
          },
        }));

    const sectionJson: FinancialAnalysisSectionJson = {
      type: 'financial-analysis-details',
      institutionName:
        this.institutionDisplayName,
      periodColumns:
        this.getPeriodColumns(cleanRows),
      rows: cleanRows,
    };

    const payload: SaveCreditProposalSectionPayload = {
      crprtOnbrdngSeq:
        this.onboardingId,

      crdtPrpslSeq:
        proposalSequence,

      blockList: [
        {
          crdtPrpslBlkSeq:
            this.crdtPrpslBlkSeq,

          refValSeq:
            this.financialAnalysisRefValSeq,

          blkTyp: 3,

          delFlg: 0,

          sortOrdr: 1,

          txtData: '',

          jsonData: [sectionJson],
        },
      ],
    };

    this.saving = true;

    const saveSubscription =
      this.creditService
        .saveSection(payload)
        .pipe(
          finalize(() => {
            this.saving = false;
          }),
        )
        .subscribe({
          next: (response: any) => {
            this.selectedRows = cleanRows;

            if (!this.config) {
              this.config = {};
            }

            this.config.selectedRows =
              cleanRows;

            this.selectedRowsChange.emit(
              cleanRows,
            );

            this.hasSavedBlock = true;

            this.savedSelectionKeys =
              new Set(
                cleanRows.map((row) =>
                  this.buildRowKey(row),
                ),
              );

            const returnedProposalSequence =
              Number(
                response?.crdtPrpslSeq ??
                response?.data
                  ?.crdtPrpslSeq ??
                proposalSequence,
              );

            if (
              returnedProposalSequence > 0
            ) {
              this.crdtPrpslSeq =
                returnedProposalSequence;

              this.creditService
                .setCrdtPrpslSeq(
                  returnedProposalSequence,
                );
            }

            const returnedBlockSequence =
              this.resolveReturnedBlockSequence(
                response,
              );

            if (
              returnedBlockSequence !== null
            ) {
              this.crdtPrpslBlkSeq =
                returnedBlockSequence;

              this.completeSave();

              return;
            }

            // Backend didn't echo the block sequence — recover it once so
            // the next save updates this record instead of duplicating it.
            const recoverySubscription =
              this.creditService
                .getCrdtBlck(
                  returnedProposalSequence,
                  [
                    this
                      .financialAnalysisRefValSeq,
                  ],
                )
                .subscribe({
                  next: (recoveryResponse) => {
                    const recovered =
                      this.extractSavedSelection(
                        recoveryResponse,
                      );

                    this.crdtPrpslBlkSeq =
                      recovered.crdtPrpslBlkSeq;

                    this.completeSave();
                  },

                  error: () => {
                    // The save itself already succeeded; a failed recovery
                    // lookup should not surface as a save failure.
                    this.completeSave();
                  },
                });

            this.subscriptions.add(
              recoverySubscription,
            );
          },

          error: (error: any) => {
            console.error(
              'Save Financial Analysis Details error:',
              error,
            );

            this.errorMessage =
              error?.error?.message ??
              this.translate.instant('FIN_ANALYSIS.ERR_SAVE');
            this.toastr.error(this.errorMessage);

            this.creditService.reportSectionSaveResult({
              sectionId: this.sectionId,
              status: 'api-error',
              message: this.errorMessage,
            });
          },
        });

    this.subscriptions.add(
      saveSubscription,
    );
  }

  private completeSave(): void {
    this.successMessage =
      this.translate.instant('FIN_ANALYSIS.SAVED_SUCCESS');
    this.toastr.success(this.successMessage);

    this.creditService.reportSectionSaveResult({
      sectionId: this.sectionId,
      status: 'saved',
    });
  }

  private reportValidationError(
    message: string,
  ): void {
    this.errorMessage = message;
    this.toastr.error(message);

    this.creditService.reportSectionSaveResult({
      sectionId: this.sectionId,
      status: 'validation-error',
      message,
    });
  }

  private resolveReturnedBlockSequence(
    response: any,
  ): number | null {
    const direct =
      this.toNullableNumber(
        response?.crdtPrpslBlkSeq ??
        response?.data?.crdtPrpslBlkSeq,
      );

    if (direct !== null) {
      return direct;
    }

    const blockList =
      Array.isArray(
        response?.blockList,
      )
        ? response.blockList
        : Array.isArray(
            response?.data?.blockList,
          )
          ? response.data.blockList
          : [];

    return this.toNullableNumber(
      blockList[0]?.crdtPrpslBlkSeq,
    );
  }

  /* ====================================================== */
  /* HELPERS */
  /* ====================================================== */

  private resetTable(): void {
    this.rows = [];
    this.selectedRows = [];
    this.periodColumns = [];
    this.selectAllChecked = false;
  }

  private updateSelectionState(): void {
    this.selectAllChecked =
      this.rows.length > 0 &&
      this.rows.every(
        (
          row:
            SelectableFinancialAnalysisRow,
        ) => row.selected,
      );

    this.updateSelectedRows();
  }

  private updateSelectedRows(): void {
    this.selectedRows =
      this.getSelectedRows();

    if (this.config) {
      this.config.selectedRows = [
        ...this.selectedRows,
      ];
    }

    this.selectedRowsChange.emit(
      this.selectedRows,
    );
  }

  private applyExistingSelections(): void {
    const fallbackKeys =
      this.hasSavedBlock
        ? this.savedSelectionKeys
        : this.getConfigSelectionKeys();

    this.rows =
      this.rows.map(
        (
          row:
            SelectableFinancialAnalysisRow,
        ) => ({
          ...row,

          selected:
            fallbackKeys.has(
              this.buildRowKey(row),
            ) ||
            this.isDefaultSelection(row),
        }),
      );

    this.updateSelectionState();
  }

  /**
   * True for a DEFAULT_SELECTED_ROWS row while nothing has been saved for
   * this section and no selection is staged in `config.selectedRows` (the
   * template starts it as []), so a saved or staged selection always wins.
   */
  private isDefaultSelection(
    row: {
      sortOrder?: number | null;
      fncTypSeq?: number | null;
      fncValSeq?: number | null;
    },
  ): boolean {
    if (
      this.hasSavedBlock ||
      (Array.isArray(this.config?.selectedRows) &&
        this.config.selectedRows.length > 0)
    ) {
      return false;
    }

    return DEFAULT_SELECTED_ROWS.some(
      (item) =>
        item.sortOrder === Number(row.sortOrder ?? 0) &&
        item.fncTypSeq === Number(row.fncTypSeq ?? 0) &&
        item.fncValSeq === Number(row.fncValSeq ?? 0),
    );
  }

  private getConfigSelectionKeys():
    Set<string> {
    const existingRows:
      FinancialAnalysisRow[] =
        Array.isArray(
          this.config?.selectedRows,
        )
          ? this.config.selectedRows
          : [];

    return new Set(
      existingRows.map((row) =>
        this.buildRowKey(row),
      ),
    );
  }

  /**
   * fncTypSeq/fncValSeq repeat across sortOrders (e.g. 2|13 under sortOrder
   * 4 and 5), so sortOrder is part of the key — without it one staged row
   * would tick every row sharing its seqs.
   */
  private buildRowKey(
    row: RowIdentity,
  ): string {
    return [
      Number(row.sortOrder ?? 0),
      Number(row.fncTypSeq ?? 0),
      Number(row.fncValSeq ?? 0),
      String(row.institution ?? '')
        .trim()
        .toLowerCase(),
    ].join('|');
  }

  private extractSavedSelection(
    response: unknown,
  ): {
    crdtPrpslBlkSeq: number | null;
    selectionKeys: Set<string>;
    hasSavedBlock: boolean;
  } {
    const blockRows =
      this.extractBlockRows(
        response,
      );

    const activeRow =
      blockRows.find(
        (item: any) =>
          Number(
            item?.refValSeq,
          ) ===
            this
              .financialAnalysisRefValSeq &&
          Number(
            item?.delFlg ?? 0,
          ) !== 1,
      );

    if (!activeRow) {
      return {
        crdtPrpslBlkSeq: null,
        selectionKeys: new Set(),
        hasSavedBlock: false,
      };
    }

    const crdtPrpslBlkSeq =
      this.toNullableNumber(
        activeRow?.crdtPrpslBlkSeq,
      );

    const savedRows =
      this.extractSavedRows(
        activeRow?.jsonData,
      );

    const selectionKeys =
      new Set(
        savedRows.map((row: any) =>
          this.buildRowKey({
            institution:
              String(
                row?.institution ?? '',
              ),
            sortOrder:
              Number(
                row?.sortOrder ?? 0,
              ),
            fncTypSeq:
              Number(
                row?.fncTypSeq ?? 0,
              ),
            fncValSeq:
              Number(
                row?.fncValSeq ?? 0,
              ),
          }),
        ),
      );

    return {
      crdtPrpslBlkSeq,
      selectionKeys,
      hasSavedBlock: true,
    };
  }

  private extractBlockRows(
    response: any,
  ): any[] {
    const possibleLists = [
      response,
      response?.data,
      response?.content,
      response?.data?.content,
      response?.blockList,
      response?.data?.blockList,
      response?.crdtPrpslBlkList,
      response?.data?.crdtPrpslBlkList,
    ];

    const matchedList =
      possibleLists.find(
        (value: unknown) =>
          Array.isArray(value),
      );

    return Array.isArray(matchedList)
      ? matchedList
      : [];
  }

  private extractSavedRows(
    jsonData: unknown,
  ): any[] {
    const normalized =
      this.normalizeSavedJsonData(
        jsonData,
      );

    const firstItem = normalized[0];

    if (
      !firstItem ||
      typeof firstItem !== 'object'
    ) {
      return [];
    }

    if (
      Array.isArray(
        (firstItem as any).rows,
      )
    ) {
      return (firstItem as any).rows;
    }

    // Backward compatibility with an older saved shape.
    if (
      Array.isArray(
        (firstItem as any).selectedRows,
      )
    ) {
      return (firstItem as any)
        .selectedRows;
    }

    return [];
  }

  private normalizeSavedJsonData(
    value: unknown,
  ): unknown[] {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return [];
    }

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'object') {
      return [value];
    }

    if (typeof value === 'string') {
      try {
        const parsed =
          JSON.parse(value);

        return this.normalizeSavedJsonData(
          parsed,
        );
      } catch (error) {
        console.warn(
          'Invalid Financial Analysis Details jsonData:',
          value,
          error,
        );

        return [];
      }
    }

    return [];
  }

  private extractRows(
    response: any,
  ): FinancialAnalysisRow[] {
    const possibleLists = [
      response,
      response?.data,
      response?.content,
      response?.data?.content,
      response?.fncAnalysisList,
      response?.data
        ?.fncAnalysisList,
      response?.financialAnalysisList,
      response?.data
        ?.financialAnalysisList,
      response?.fncAnlsysList,
      response?.data
        ?.fncAnlsysList,
    ];

    const list =
      possibleLists.find(
        (value: unknown) =>
          Array.isArray(value),
      );

    return Array.isArray(list)
      ? list
      : [];
  }

  private normalizePeriodValues(
    periodValues: unknown,
  ): Record<string, number | null> {
    if (
      periodValues === null ||
      periodValues === undefined ||
      typeof periodValues !== 'object' ||
      Array.isArray(periodValues)
    ) {
      return {};
    }

    const normalized:
      Record<string, number | null> = {};

    Object.entries(
      periodValues as
        Record<string, unknown>,
    ).forEach(
      ([period, value]) => {
        if (
          value === null ||
          value === undefined ||
          String(value).trim() === ''
        ) {
          normalized[period] = null;

          return;
        }

        const numericValue =
          Number(value);

        normalized[period] =
          Number.isFinite(
            numericValue,
          )
            ? numericValue
            : null;
      },
    );

    return normalized;
  }

  private getPeriodColumns(
    rows: Array<{
      periodValues: Record<string, number | null>;
    }>,
  ): string[] {
    const periods =
      new Set<string>();

    // Only periods with a value in at least one row: a year/statement type
    // without saved figures (e.g. Unaudited for a year saved as Audited)
    // would otherwise show — and be saved for the report — as an empty column.
    rows.forEach(
      (row) => {
        Object.entries(
          row.periodValues ?? {},
        ).forEach(
          ([period, value]) => {
            if (
              value !== null &&
              value !== undefined
            ) {
              periods.add(period);
            }
          },
        );
      },
    );

    return Array.from(
      periods,
    ).sort(
      (
        first: string,
        second: string,
      ) =>
        first.localeCompare(
          second,
          undefined,
          {
            numeric: true,
            sensitivity: 'base',
          },
        ),
    );
  }

  private toNullableNumber(
    value: unknown,
  ): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const numericValue =
      Number(value);

    return Number.isFinite(
      numericValue,
    )
      ? numericValue
      : null;
  }

  private getOnboardingSequence():
    number {
    let currentRoute:
      ActivatedRoute | null =
      this.route;

    while (currentRoute) {
      const routeValue =
        currentRoute.snapshot
          .paramMap.get('id') ??
        currentRoute.snapshot
          .paramMap.get('oid');

      const sequence =
        Number(routeValue);

      if (
        routeValue &&
        Number.isFinite(sequence) &&
        sequence > 0
      ) {
        return sequence;
      }

      currentRoute =
        currentRoute.parent;
    }

    return 0;
  }
}
